
import { GeneticsMap, GeneticsMapGroup } from '@/services/crossbreeding-service/models';
import { Component, Vue, Prop } from 'vue-property-decorator';
import SimulationMapGroup from './SimulationMapGroup.vue';

@Component({
  components: { SimulationMapGroup }
})
export default class SimulationResults extends Vue {
  @Prop({ type: Array }) readonly mapGroups: GeneticsMapGroup[];
  @Prop({ type: Object }) readonly highlightedMap: GeneticsMap;
  @Prop({ type: Number, default: 0 }) readonly filtersOffsetPx!: number;
  @Prop({ type: Array, default: () => [] }) readonly sourceGenes!: string[];

  filteringGenes: { [key: string]: string | null } = {
    gene0: '',
    gene1: '',
    gene2: '',
    gene3: '',
    gene4: '',
    gene5: ''
  };

  geneCount: { [key: string]: string | null } = {
    gCount: '',
    yCount: '',
    hCount: ''
  };

  get geneCountRules() {
    const a = String(this.$t('results.ruleMinZero'));
    const b = String(this.$t('results.ruleMaxSix'));
    const c = String(this.$t('results.ruleWhole'));
    return [(v: number) => !v || v >= 0 || a, (v: number) => !v || v <= 6 || b, (v: number) => !v || v % 1 === 0 || c];
  }

  /** Hard cap of visible crossbreeds for cleaner UI. */
  readonly maxVisibleCrossbreeds = 3;
  isFormValid = true;

  get filteredMapGroups() {
    let mapGroups = this.mapGroups || [];

    if (!this.isFormValid) {
      return mapGroups;
    }

    const countChecks: Array<{ key: string; method: (s: any) => number }> = [
      { key: 'gCount', method: (s) => s.numberOfGs() },
      { key: 'yCount', method: (s) => s.numberOfYs() },
      { key: 'hCount', method: (s) => s.numberOfHs() }
    ];
    for (const { key, method } of countChecks) {
      if (this.geneCount[key]) {
        const target = Number(this.geneCount[key]);
        mapGroups = mapGroups.filter((group) => method(group.mapList[0].resultSapling) === target);
      }
    }

    if (this.hasGeneFiltersApplied) {
      mapGroups = mapGroups.filter((group) => {
        let allGenesMatch = true;
        for (let i = 0; i < 6; i++) {
          if (
            this.filteringGenes[`gene${i}`] !== '' &&
            !(this.filteringGenes[`gene${i}`] || '')
              .toUpperCase()
              .split('/')
              .includes(group.mapList[0].resultSapling.genes[i].type)
          ) {
            allGenesMatch = false;
          }
        }
        return allGenesMatch;
      });
    }

    return mapGroups;
  }

  get hasGeneFiltersApplied() {
    return (
      this.filteringGenes.gene0 !== '' ||
      this.filteringGenes.gene1 !== '' ||
      this.filteringGenes.gene2 !== '' ||
      this.filteringGenes.gene3 !== '' ||
      this.filteringGenes.gene4 !== '' ||
      this.filteringGenes.gene5 !== ''
    );
  }

  get visibleMapGroups() {
    return this.filteredMapGroups.slice(0, this.maxVisibleCrossbreeds);
  }

  get filterGeneString(): string | null {
    const slots = [0, 1, 2, 3, 4, 5].map((i) => (this.filteringGenes[`gene${i}`] || '').toUpperCase().trim());
    if (slots.some((s) => s === '' || s.includes('/'))) return null;
    return slots.join('');
  }

  get filterMatchesSourceGene(): number | null {
    if (!this.filterGeneString) return null;
    const idx = this.sourceGenes.findIndex((g) => g.toUpperCase() === this.filterGeneString);
    return idx >= 0 ? idx + 1 : null;
  }

  get filterCountConflict(): string | null {
    if (!this.filterGeneString) return null;
    const t = this.filterGeneString;
    const actual = {
      g: t.split('').filter((c) => c === 'G').length,
      y: t.split('').filter((c) => c === 'Y').length,
      h: t.split('').filter((c) => c === 'H').length
    };
    const conflicts: string[] = [];
    if (this.geneCount.gCount && Number(this.geneCount.gCount) !== actual.g)
      conflicts.push(`${t} has ${actual.g} Gs, not ${this.geneCount.gCount}`);
    if (this.geneCount.yCount && Number(this.geneCount.yCount) !== actual.y)
      conflicts.push(`${t} has ${actual.y} Ys, not ${this.geneCount.yCount}`);
    if (this.geneCount.hCount && Number(this.geneCount.hCount) !== actual.h)
      conflicts.push(`${t} has ${actual.h} Hs, not ${this.geneCount.hCount}`);
    return conflicts.length > 0 ? conflicts.join(', ') : null;
  }

  get filtersFormStyle() {
    return this.filtersOffsetPx !== 0 ? { marginTop: `${this.filtersOffsetPx}px` } : {};
  }

  mounted() {
    const p = new URLSearchParams(window.location.search);
    if (p.get('gCount')) this.geneCount.gCount = p.get('gCount');
    if (p.get('yCount')) this.geneCount.yCount = p.get('yCount');
    if (p.get('hCount')) this.geneCount.hCount = p.get('hCount');
    for (let i = 0; i < 6; i++) {
      const v = p.get(`g${i}`);
      if (v) this.filteringGenes[`gene${i}`] = v;
    }
  }

  stepCount(key: string, delta: number) {
    const current = Number(this.geneCount[key]) || 0;
    const next = Math.max(0, Math.min(6, current + delta));
    this.geneCount[key] = next === 0 ? '' : String(next);
  }

  handleMapSelectedEvent(map: GeneticsMap) {
    this.$emit('map-selected', map);
  }

  handleGroupSelectedEvent(group: GeneticsMapGroup) {
    this.$emit('group-selected', group);
  }

  resetPage() {
    // Intentionally left for API compatibility with parent component.
  }
}
